import classNames from 'classnames'
import { format } from 'date-fns'
import { HiOutlineChevronLeft, HiOutlineChevronRight, HiOutlineXCircle } from 'react-icons/hi'
import { Component } from 'react'
import { toast } from 'react-toastify'
import { IWindowOptions, Window } from '.'
import { PaginationAwareObject } from '../api'
import { getSoftwareVersion, getSoftwareVersionPagination, ISoftwareVersion } from '../api/0x0002/software-versions'
import { getLocale, getString } from '../locale'
import { ClientModel } from '../api/clients'

export async function askSoftwareVersion(model: ClientModel, options?: IWindowOptions): Promise<ISoftwareVersion | undefined> {
    let pickRef: SoftwareVersionPick | null = null
    const { value, isConfirmed } = await new Window().fire({
        ...options,
        title: getString('ota.select_software'),
        showCancelButton: true,
        showLoaderOnConfirm: true,
        allowEnterKey: true,
        body: <SoftwareVersionPick ref={ref => (pickRef = ref)} model={model} />,
        preConfirm: async () => {
            try {
                const { selectedSoftwareVersionId } = pickRef!.state
                if (selectedSoftwareVersionId === undefined) {
                    throw new Error('😞')
                }
                const softwareVersion = (await getSoftwareVersion(selectedSoftwareVersionId)).data
                return softwareVersion
            } catch (err) {
                console.error(err)
                toast.error((err as Error).message)
                return false
            }
        },
    })
    if (!isConfirmed) return undefined
    return value
}

type Props = {
    model: ClientModel
}

type State = {
    softwareVersionId?: ISoftwareVersion['id']
    softwareVersions?: ISoftwareVersion[]
    currentPage: PaginationAwareObject<ISoftwareVersion>['current_page']
    prevPage: PaginationAwareObject<ISoftwareVersion>['prev_page']
    nextPage: PaginationAwareObject<ISoftwareVersion>['next_page']
    disablePagination: boolean
    selectedSoftwareVersionId?: ISoftwareVersion['id']
}

class SoftwareVersionPick extends Component<Props, State> {
    state: State = {
        softwareVersionId: undefined,
        softwareVersions: [],
        currentPage: 1,
        prevPage: undefined,
        nextPage: undefined,
        disablePagination: false,
        selectedSoftwareVersionId: undefined,
    }

    componentDidMount = () => this.loadSoftwareVersions()

    async loadSoftwareVersions() {
        try {
            const { currentPage } = this.state
            const result = (await getSoftwareVersionPagination(currentPage, this.props.model)).data
            this.setState({
                softwareVersions: result.data,
                currentPage: result.current_page,
                prevPage: result.prev_page,
                nextPage: result.next_page,
            })
        } catch (err) {
            toast.error((err as Error).message)
            console.error(err)
        }
        this.setState({ disablePagination: false })
    }

    handleSoftwareVersionClick(softwareVersion: ISoftwareVersion) {
        this.setState({ selectedSoftwareVersionId: softwareVersion.id })
    }

    render() {
        return (
            <>
                {this.renderPagination()}
                {this.renderSoftwareVersions()}
            </>
        )
    }

    renderPagination() {
        const { prevPage, nextPage, disablePagination } = this.state
        return (
            <div className={classNames('mb-5 flex justify-between', getLocale() === 'he' ? 'flex-row' : 'flex-row-reverse')}>
                {this.renderPageOption(
                    <>
                        <div>{getString('common.previous')}</div>
                        <div>{<HiOutlineChevronLeft className={classNames(getLocale() === 'he' && 'flip-h')} />}</div>
                    </>,
                    async () => {
                        if (prevPage === null || prevPage === undefined || disablePagination) return
                        this.setState({ currentPage: prevPage, disablePagination: true }, () => this.loadSoftwareVersions())
                    },
                    prevPage === null || prevPage === undefined || disablePagination,
                )}
                {this.renderPageOption(
                    <>
                        <div>{<HiOutlineChevronRight className={classNames(getLocale() === 'he' && 'flip-h')} />}</div>
                        <div>{getString('common.next')}</div>
                    </>,
                    async () => {
                        if (nextPage === null || nextPage === undefined || disablePagination) return
                        this.setState({ currentPage: nextPage, disablePagination: true }, () => this.loadSoftwareVersions())
                    },
                    nextPage === null || nextPage === undefined || disablePagination,
                )}
            </div>
        )
    }

    renderPageOption(el: JSX.Element, onClick: () => void, disabled = false) {
        return (
            <div
                className={classNames(
                    'flex flex-row items-center p-4 bg-white dark:bg-gray-700 dark:text-white shadow-md rounded-xl',
                    disabled ? 'bg-gray-200 dark:bg-opacity-40 cursor-not-allowed' : 'hover-grow',
                    getLocale() === 'he' ? 'flex-row-reverse' : 'flex-row',
                )}
                onClick={onClick}
            >
                {el}
            </div>
        )
    }

    renderSoftwareVersions() {
        const { softwareVersions } = this.state
        if (softwareVersions === undefined) return undefined
        if (softwareVersions.length === 0)
            return (
                <div className="w-full h-full dark:text-white flex flex-row items-center justify-center">
                    <HiOutlineXCircle className="me-1" />
                    <h1>{getString('software_manager.no_software_versions')}</h1>
                </div>
            )
        return <div className="w-full px-10">{softwareVersions.map(this.renderSoftwareVersion.bind(this))}</div>
    }

    renderSoftwareVersion(softwareVersion: ISoftwareVersion, i: number) {
        const selected = this.state.selectedSoftwareVersionId === softwareVersion.id
        return (
            <div
                key={i}
                className={classNames(
                    'w-full rounded-xl shadow-md overflow-hidden md:hover-grow',
                    i !== 0 && 'mt-5',
                    selected ? 'bg-yellow-500' : 'bg-white dark:bg-gray-800',
                )}
                onClick={() => this.handleSoftwareVersionClick(softwareVersion)}
            >
                <div className="md:flex md:items-center md:content-center md:ms-3">
                    <div className="md:flex-shrink-0">
                        <div className="text-5xl font-bold text-center object-cover md:w-24 dark:text-gray-400">{softwareVersion.id}</div>
                    </div>
                    <div className="p-4 md:py-4">
                        <div className="tracking-wide text-2xl text-indigo-500 font-semibold">{softwareVersion.version}</div>
                        {softwareVersion.createdAt && (
                            <div className="mt-2 text-sm dark:text-white">
                                {format(new Date(softwareVersion.createdAt), getString('software_manager.created_at_format'))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        )
    }
}
